import React, { useEffect, useMemo, useState } from 'react';
import { Container, Table } from 'react-bootstrap';
import { Row } from 'reactstrap';
import useGetFieldAlyticsApiRequests, { FieldAlyticsApiRequest } from '../../requests/fieldalytics/useGetFieldAlyticsApiRequests';
import { PaginatedResult } from '../../requests/usePaginatedQuery';
import PaginatedTable from '../Common/Pagination/PaginatedTable';
import QueryView from '../Common/QueryView';
import BoldButton from '../Common/BoldButton';
import KaModal from '../Common/KaModal';
import InfoTooltip from '../Common/InfoTooltip';
import { clipJson, formatJson, formatTimestamp, httpStatusText, statusClassName } from '../Common/ApiEventLogFormatting';

const directionDefinitions = <div className="text-start text-nowrap">
  <div><strong>Inbound</strong> — requests FieldAlytics sent to Constellation</div>
  <div><strong>Outbound</strong> — requests Constellation sent to FieldAlytics</div>
</div>;

const PAGE_SIZE = 50;

interface RequestRowProps {
  request: FieldAlyticsApiRequest;
  onShowRequest: (request: FieldAlyticsApiRequest) => void;
}

const RequestRow = ({ request, onShowRequest }: RequestRowProps) => {
  const [responseExpanded, setResponseExpanded] = useState(false);
  const { preview, remaining } = useMemo(() => clipJson(request.responseBody), [request.responseBody]);
  const responseBody = useMemo(() => formatJson(request.responseBody), [request.responseBody]);

  return (
    <tr>
      <td>{request.direction}</td>
      <td>{formatTimestamp(request.timestamp)}</td>
      <td className="text-break"><div>{request.method}</div><div>{request.path}</div></td>
      <td>
        {request.responseStatusCode === null
          ? '—'
          : <span className={statusClassName(request.responseStatusCode)}>
              {request.responseStatusCode} {httpStatusText(request.responseStatusCode)}
            </span>}
      </td>
      <td>
        <pre className="mb-0 small preserve-whitespace text-break">
          {responseExpanded ? responseBody : preview}
        </pre>
        {!responseExpanded && remaining > 0 && (
          <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => setResponseExpanded(true)}>
            [+{remaining} more line{remaining === 1 ? '' : 's'} — click to expand]
          </button>
        )}
      </td>
      <td align="right">
        <button type="button" className="btn btn-primary btn-sm fw-bold" onClick={() => onShowRequest(request)}>
          View Request
        </button>
      </td>
    </tr>
  );
};

const RequestModal = ({ request, onHide }: { request: FieldAlyticsApiRequest; onHide: () => void }) => (
  <KaModal
    show={true}
    onHide={onHide}
    size="lg"
    title="Request Details"
    body={<>
      <div className="row gx-4 gy-2 mb-3">
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Timestamp</div>
          <div>{formatTimestamp(request.timestamp)}</div>
        </div>
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Direction</div>
          <div>{request.direction}</div>
        </div>
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Method</div>
          <div>{request.method}</div>
        </div>
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Status Code</div>
          <div>{request.responseStatusCode ?? '—'}</div>
        </div>
        <div className="col-12">
          <div className="small fw-semibold text-dark">Path</div>
          <div>{request.path}</div>
        </div>
      </div>
      <div className="row gx-3">
        <div className="col">
          <div className="small fw-semibold text-dark mb-1">Request Body</div>
          <pre className="border rounded p-2 bg-white small mb-0 preserve-whitespace text-break">{formatJson(request.body)}</pre>
        </div>
        <div className="col">
          <div className="small fw-semibold text-dark mb-1">Response Body</div>
          <pre className="border rounded p-2 bg-white small mb-0 preserve-whitespace text-break">{formatJson(request.responseBody)}</pre>
        </div>
      </div>
    </>}
    footer={
      <button type="button" className="btn btn-ghost-secondary" onClick={onHide}>Close</button>
    }
  />
);

export const FieldAlyticsApiEventLogPage = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [modalRequest, setModalRequest] = useState<FieldAlyticsApiRequest | null>(null);

  const getRequests = useGetFieldAlyticsApiRequests({ page: currentPage, pageSize: PAGE_SIZE });

  const totalPages = getRequests.data?.totalPages;

  useEffect(() => {
    if (totalPages !== undefined && currentPage >= totalPages) setCurrentPage(0);
  }, [totalPages, currentPage]);

  return (
    <Container fluid>
        <Row className="justify-content-between mb-2 align-items-center">
          <h2 className="col-auto ka-blue">FieldAlytics - Event Log</h2>
          <div className="col-auto d-flex align-items-center gap-1">
            <BoldButton className="btn-ghost-secondary px-2" onClick={() => getRequests.query()}>
              <i className="bi-arrow-clockwise me-1" />Refresh
            </BoldButton>
          </div>
        </Row>
        <div className="text-muted small mb-3">
          <strong>Note:</strong> completed orders are sent to FieldAlytics every 2 minutes
        </div>
        <QueryView
          query={getRequests}
          renderData={(data: PaginatedResult<FieldAlyticsApiRequest>) => (
            data.items.length === 0
              ? <p>No API requests found.</p>
              : <PaginatedTable
                  onPageChange={page => setCurrentPage(page)}
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  totalPages={data.totalPages}>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th className="align-top text-nowrap">
                          Direction <InfoTooltip wide content={directionDefinitions} />
                        </th>
                        <th className="align-top">Timestamp</th>
                        <th className="align-top" style={{ width: '25%' }}>Path</th>
                        <th className="align-top">Status</th>
                        <th className="align-top" style={{ width: '40%' }}>Response Body</th>
                        <th className="align-top" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map(request =>
                        <RequestRow
                          key={request.id}
                          request={request}
                          onShowRequest={setModalRequest} />
                      )}
                    </tbody>
                  </Table>
                </PaginatedTable>
          )} />
        {modalRequest && <RequestModal request={modalRequest} onHide={() => setModalRequest(null)} />}
    </Container>
  );
};
